/**
 * Prototype urgency scoring for the Live Message Feed.
 *
 * In the production design this score will come from a backend AI ranker that
 * reads the inbound message's transcription and returns an urgency 0–99 along
 * with the contributing factors. For the prototype we deterministically hash
 * the item id so each row gets a stable, repeatable "score" and a stable
 * "explanation" (factor list) without flicker on re-render.
 *
 * When the real backend lands, replace the body of `getUrgencyForItem` with a
 * lookup against whatever the server publishes (likely the score + factors
 * joined into `messageFeedQuery`). The shape of `MessageUrgency` is the
 * intended contract.
 */

export type UrgencyLevel = "critical" | "high" | "medium" | "low";

export type UrgencyFactorKind = "tone" | "keyword" | "status" | "thread" | "load" | "timing";

export type UrgencyFactor = {
  kind: UrgencyFactorKind;
  label: string;
  detail: string;
  delta: number;
};

export type MessageUrgency = {
  score: number;
  level: UrgencyLevel;
  color: string;
  label: string;
  factors: UrgencyFactor[];
  summary: string;
};

const TIERS: { min: number; level: UrgencyLevel; color: string; label: string }[] = [
  { min: 85, level: "critical", color: "#f87171", label: "Critical" },
  { min: 65, level: "high", color: "#fb923c", label: "High" },
  { min: 35, level: "medium", color: "#fbbf24", label: "Medium" },
  { min: 0, level: "low", color: "#9ca3af", label: "Low" },
];

const BASE_SCORE = 30;

// Realistic-looking factors a future AI ranker might surface. Deltas are
// rough order-of-magnitude weights; the real model will tune these.
const FACTOR_POOL: UrgencyFactor[] = [
  // --- keyword / content signals ---------------------------------------
  { kind: "keyword", label: "Safety keyword", detail: "matched: 'accident', 'crash'", delta: 35 },
  { kind: "keyword", label: "Breakdown keyword", detail: "matched: 'broke down', 'won't start'", delta: 28 },
  { kind: "keyword", label: "Stuck / blocked", detail: "matched: 'stuck', 'can't move'", delta: 18 },
  { kind: "keyword", label: "DOT / inspection", detail: "matched: 'DOT', 'inspection', 'officer'", delta: 16 },
  { kind: "keyword", label: "Fuel concern", detail: "matched: 'low fuel', 'running out'", delta: 12 },
  { kind: "keyword", label: "Detention / waiting", detail: "matched: 'still waiting', 'detained'", delta: 10 },
  { kind: "keyword", label: "ETA question", detail: "matched: 'when', 'how long', 'ETA'", delta: 6 },
  { kind: "keyword", label: "Load handoff", detail: "matched: 'unloaded', 'delivered'", delta: 3 },
  { kind: "keyword", label: "Routine update", detail: "no priority keywords detected", delta: -3 },

  // --- tone / sentiment signals ----------------------------------------
  { kind: "tone", label: "Distressed tone", detail: "phrases like 'help', 'really need', 'please'", delta: 22 },
  { kind: "tone", label: "Frustrated tone", detail: "emphasis, repeated phrasing, all-caps fragments", delta: 14 },
  { kind: "tone", label: "Curt / terse tone", detail: "very short message, abrupt phrasing", delta: 6 },
  { kind: "tone", label: "Neutral tone", detail: "informational delivery", delta: 0 },
  { kind: "tone", label: "Calm tone", detail: "no urgency markers in phrasing", delta: -4 },

  // --- driver status signals -------------------------------------------
  { kind: "status", label: "On-Duty stationary 2h+", detail: "extended dwell while on duty", delta: 14 },
  { kind: "status", label: "Driving", detail: "actively hauling — response time matters", delta: 4 },
  { kind: "status", label: "On-Duty (not driving)", detail: "stopped on duty, normal", delta: 0 },
  { kind: "status", label: "Off-Duty (rest)", detail: "less time-sensitive while resting", delta: -6 },
  { kind: "status", label: "Sleeper Berth", detail: "off-network in sleeper", delta: -10 },

  // --- conversation / thread signals -----------------------------------
  { kind: "thread", label: "No dispatch reply 45m+", detail: "open inbound, no response yet", delta: 16 },
  { kind: "thread", label: "3rd message in 15m", detail: "driver retrying — likely escalating", delta: 12 },
  { kind: "thread", label: "Driver replied to dispatch", detail: "follow-up to outbound message", delta: 4 },
  { kind: "thread", label: "First contact today", detail: "no prior context this shift", delta: 2 },
  { kind: "thread", label: "Open thread (recent reply)", detail: "back-and-forth in progress", delta: -3 },

  // --- load / context signals ------------------------------------------
  { kind: "load", label: "Hazmat load", detail: "load is hazmat-rated", delta: 10 },
  { kind: "load", label: "Time-sensitive POD", detail: "delivery window <2h", delta: 8 },
  { kind: "load", label: "High-value cargo", detail: "load tagged 'High-Value'", delta: 6 },
  { kind: "load", label: "Reefer load", detail: "temperature-controlled — monitor closely", delta: 5 },

  // --- timing signals --------------------------------------------------
  { kind: "timing", label: "Off-hours arrival", detail: "outside dispatcher coverage window", delta: 7 },
  { kind: "timing", label: "Approaching HOS limit", detail: "<1h of available drive time", delta: 9 },
  { kind: "timing", label: "Inside business hours", detail: "normal coverage window", delta: -2 },
];

function djb2(input: string): number {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) + hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function hashAt(input: string, salt: string): number {
  return djb2(`${salt}::${input}`);
}

function poolByKind(): Record<UrgencyFactorKind, UrgencyFactor[]> {
  const groups: Record<UrgencyFactorKind, UrgencyFactor[]> = {
    keyword: [],
    tone: [],
    status: [],
    thread: [],
    load: [],
    timing: [],
  };
  for (const f of FACTOR_POOL) groups[f.kind].push(f);
  return groups;
}

const POOLS = poolByKind();

function pickOne(itemId: string, kind: UrgencyFactorKind, salt: string): UrgencyFactor {
  const pool = POOLS[kind];
  return pool[hashAt(itemId, salt) % pool.length];
}

function pickFactors(itemId: string): UrgencyFactor[] {
  const factors: UrgencyFactor[] = [];

  // Always exactly one tone, one status, one thread signal — that's the
  // "spine" of any explanation a dispatcher would expect to see.
  factors.push(pickOne(itemId, "tone", "tone"));
  factors.push(pickOne(itemId, "status", "status"));
  factors.push(pickOne(itemId, "thread", "thread"));

  // 0–2 keyword signals, weighted so most messages have one.
  const keywordCount = [0, 1, 1, 1, 2][hashAt(itemId, "kc") % 5];
  const seenKeywords = new Set<string>();
  for (let i = 0; i < keywordCount; i++) {
    const candidate = pickOne(itemId, "keyword", `k${i}`);
    if (!seenKeywords.has(candidate.label)) {
      seenKeywords.add(candidate.label);
      factors.push(candidate);
    }
  }

  // ~25% of messages get a load-context signal.
  if (hashAt(itemId, "ld") % 4 === 0) {
    factors.push(pickOne(itemId, "load", "load"));
  }

  // ~33% pick up a timing signal.
  if (hashAt(itemId, "tm") % 3 === 0) {
    factors.push(pickOne(itemId, "timing", "timing"));
  }

  return factors;
}

// The score for a given item id is pure and stable, but the computation does
// several hashes + allocations. Cache per item id so re-renders and the
// urgency sort (which compares many pairs) don't recompute it repeatedly.
const urgencyCache = new Map<string, MessageUrgency>();

export function getUrgencyForItem(itemId: string): MessageUrgency {
  const cached = urgencyCache.get(itemId);
  if (cached) return cached;
  const factors = pickFactors(itemId);
  const deltaSum = factors.reduce((acc, f) => acc + f.delta, 0);
  const score = Math.max(0, Math.min(99, BASE_SCORE + deltaSum));
  const tier = TIERS.find((t) => score >= t.min) ?? TIERS[TIERS.length - 1];

  // Show the highest-magnitude factors first so the dispatcher sees the
  // dominant reason at a glance.
  const sorted = [...factors].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  const result: MessageUrgency = {
    score,
    level: tier.level,
    color: tier.color,
    label: tier.label,
    factors: sorted,
    summary: sorted[0]?.label ?? "No signals",
  };
  urgencyCache.set(itemId, result);
  return result;
}

export function compareUrgencyDesc(aItemId: string, bItemId: string): number {
  return getUrgencyForItem(bItemId).score - getUrgencyForItem(aItemId).score;
}
